import { FunctionComponent, useContext, useState } from 'react';
import dynamic from 'next/dynamic';
import classnames from 'classnames';

import { Types } from 'models/CartItem';

import LocaleContext from 'components/context/Locale';
import LayoutContainer from 'components/layout/LayoutContainer';
import NewImage from 'components/layout/NewImage';
import P24Instalments from 'components/layout/P24Instalments';
import AddToCartButton from 'components/modules/public/products/AddToCartButton';
import BuyNowButton from 'components/modules/public/products/BuyNowButton';
import ProductBoxPayPoModal from 'components/modules/public/products/ProductBoxPayPoModal';
import { useCountdown } from 'hooks/useCountdown';

import { Props } from './index';
import StyledComponent from './styles';

const Portal = dynamic(() => import('components/modules/public/layout/modals/Portal'), { ssr: false });
const ProductBoxBuyNowModal = dynamic(() => import('components/modules/public/products/BundleBoxBuyNowModal'), { ssr: false });

const PublicPageProductSectionProduct: FunctionComponent<Props> = ({ bundle, actions, state, specialOfferLabels }) => {
    const {
        id,
        pricing,
        showLowestPrice,
        bundleInfos,
        ongoingPromo,
        promoPercent,
        promoRecommended,
    } = bundle;
    const [buyNowModalVisible, setBuyNowModalVisible] = useState<boolean>(false);
    const [payPoModalVisible, setPayPoModalVisible] = useState<boolean>(false);
    const [days, hours, minutes, seconds] = useCountdown(bundle?.promoValidTo || '');

    const onCreateCartOffer = async () => {
        return await actions.createCartItem({
            cartId: state?.cart?.id,
            itemId: id,
            itemType: Types.Bundle,
            quantity: 1,
        });
    };

    const localeContext = useContext(LocaleContext);
    const content = localeContext?.translations?.product?.content?.product?.content;

    // Price to display
    const packagePriceDisplay = pricing?.priceDisplay;
    const packageOnGoingPromoPriceDisplay = packagePriceDisplay && ongoingPromo && ongoingPromo.pricing.priceDisplay;

    // Price for instalments component
    const priceToInstalments = ongoingPromo && promoPercent > 0
        ? ongoingPromo?.pricing?.price
        : pricing?.price;

    const isExpired = (days + hours + minutes + seconds) < 0 || isNaN((days + hours + minutes + seconds));

    return (
        <StyledComponent className="public-page-special-offer-bundle-section-bundle">
            <LayoutContainer>
                <div className="bundle">
                    <div className="image-container">
                        <NewImage
                            className="bundle-image"
                            fileUpload={bundle?.locale?.image}
                            resizerConfig={{ width: 600 }}
                            alt={bundle?.locale?.name}
                        />
                    </div>
                    <div className="content-container">
                        <div className="special-offer-labels">
                            <div className="label">{specialOfferLabels.specialOffer}</div>
                            {promoRecommended && (
                                <div className="label">{specialOfferLabels.recommended}</div>
                            )}
                            <div
                                className={classnames({ 'has-promo-valid-date': !!bundle?.promoValidTo })}
                            >
                                {bundle?.promoValidTo && !isExpired ? (
                                    <div className="label">
                                        Koniec za: {days}d : {hours}h : {minutes}m : {seconds}s
                                    </div>
                                ) : (
                                    <div className="label alert">
                                        Oferta zakończyła się
                                    </div>
                                )}
                            </div>
                        </div>
                        {(ongoingPromo && promoPercent > 0) && (
                            <div className={classnames('discount-desktop', {
                                'higher': Boolean(showLowestPrice),
                            })}
                            >
                                -{promoPercent}%
                            </div>
                        )}
                        <div className="header">
                            {bundle?.locale?.name && (
                                <div className="headline">
                                    <h1 dangerouslySetInnerHTML={{ __html: bundle?.locale?.name }} />
                                </div>
                            )}
                            <div className="mobile-image-wrapper">
                                <NewImage
                                    className="bundle-image-mobile"
                                    fileUpload={bundle?.locale?.image}
                                    resizerConfig={{ width: 600 }}
                                    alt={bundle?.locale?.name}
                                />
                                {(ongoingPromo && promoPercent > 0) && (
                                    <div className={classnames('discount-mobile', {
                                        'higher': Boolean(showLowestPrice),
                                    })}
                                    >
                                        -{promoPercent}%
                                    </div>
                                )}
                            </div>
                            <div className="old-price-wrapper">
                                {showLowestPrice && (
                                    <div className="last-month-price-container">
                                        <div className="info-mark">
                                            i
                                            <div className="info-mark__information">
                                                {'Informacja o najniższej cenie tej usługi z ostatnich 30 dni przed obecną obniżką'}
                                            </div>
                                        </div>
                                        {'Poprzednia najniższa cena:'}
                                        <span>
                                            {pricing?.lowestPriceValueisplay}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="price-data-container">
                                <div className="prices-container">
                                    {(!ongoingPromo || (ongoingPromo && promoPercent <= 0)) && (
                                        <div
                                            className="pricing-price"
                                            dangerouslySetInnerHTML={{ __html: packagePriceDisplay }}
                                        />
                                    )}
                                    {(ongoingPromo && promoPercent > 0) && (
                                        <>
                                            <div
                                                className="pricing-price"
                                                dangerouslySetInnerHTML={{ __html: packageOnGoingPromoPriceDisplay }}
                                            />
                                            <div
                                                className="pricing-old-price"
                                                dangerouslySetInnerHTML={{ __html: packagePriceDisplay }}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            {bundle?.locale?.description && (
                                <div
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: bundle?.locale?.description }}
                                />
                            )}
                        </div>

                        <div className="content-list">
                            <div className="content">
                                {bundle?.packageQuantity && (
                                    <div className="title">
                                        <img
                                            className="icon"
                                            src="/images/product-info/heart.svg"
                                            loading="lazy"
                                            alt=""
                                        />
                                        <span className="title-gold">{content?.titles?.quantity}</span>
                                        <span>{bundle?.packageQuantity}</span>
                                    </div>
                                )}
                                {bundle?.locale?.target && (
                                    <div className="title">
                                        <img
                                            className="icon"
                                            src="/images/product/target.svg"
                                            loading="lazy"
                                            alt=""
                                        />
                                        <span className="title-gold">{content?.titles?.target}</span>
                                        <span>{bundle?.locale?.target}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {bundleInfos.length > 0 && (
                            <ul className="package-list">
                                {bundleInfos
                                    .sort((a, b) => a.order - b.order)
                                    .map((bundleInfo) => (
                                        <li
                                            className="package-item"
                                            key={bundleInfo.id}
                                        >
                                            <span
                                                className={classnames('top', 'package-item-icon')}
                                            >
                                                <img
                                                    className="icon"
                                                    src={bundleInfo.icon.imageUrl}
                                                    alt={bundleInfo.icon.alt}
                                                    width="15px"
                                                    height="15px"
                                                    loading="lazy"
                                                ></img>
                                            </span>
                                            <div
                                                className="content"
                                                dangerouslySetInnerHTML={{ __html: bundleInfo.locales[0]?.content }}
                                            />
                                        </li>
                                    ))}
                            </ul>
                        )}

                        <div className="footer">
                            {(bundle?.promoValidTo && !isExpired) && (
                                <div className="footer-controls">
                                    <BuyNowButton
                                        label={'Kup Teraz'}
                                        onClick={() => setBuyNowModalVisible(true)}
                                    />
                                    <AddToCartButton
                                        label={'Do koszyka'}
                                        onClick={() => onCreateCartOffer()}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="footer-banner">
                            <P24Instalments
                                price={priceToInstalments}
                                topBorder={true}
                            />
                        </div>
                    </div>
                </div>
            </LayoutContainer>

            {buyNowModalVisible && (
                <Portal>
                    <ProductBoxBuyNowModal
                        bundle={bundle}
                        onClose={() => setBuyNowModalVisible(false)}
                    />
                </Portal>
            )}

            {payPoModalVisible && (
                <Portal>
                    <ProductBoxPayPoModal
                        onClose={() => setPayPoModalVisible(false)}
                    />
                </Portal>
            )}
        </StyledComponent>
    );
};

export default PublicPageProductSectionProduct;
